import { Checkbox, Column, ErrorMessage } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { AddressFormAutocomplete } from '@vend/business-components/dist/components/AddressForm/AddressFormAutocomplete/AddressFormAutocomplete'
import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { CountryDropdown } from '@vend/business-components/dist/components/CountryDropdown/CountryDropdown'
import { FieldColumn } from '@vend/business-components/dist/components/EditCustomerModal/common/form/FieldColumn'
import { googleMapKey } from '@vend/business-components/dist/components/EditCustomerModal/common/utils/google-map-key'
import { AddressTypes } from '@vend/business-components/dist/components/EditCustomerModal/DetailsTab/Address'
import { StateDropdown } from '@vend/business-components/dist/components/StateDropdown'
import { useStates } from '@vend/business-components/dist/components/StateDropdown/useStates'
import React from 'react'
import { useRetailerSettingsManager } from '../../../../../ng-compat/hooks'
import { FormInput } from './FormInput'
import { FormRow } from './FormRow'

const normalizeAddress = (address?: AddressDetails): AddressDetails => ({
  address_1: address?.address_1 || '',
  address_2: address?.address_2 || '',
  suburb: address?.suburb || '',
  city: address?.city || '',
  state: address?.state || '',
  country_id: address?.country_id || '',
  postcode: address?.postcode || '',
})

interface AddressProps {
  type: AddressTypes
  addressInfo: AddressDetails
  errors: AddressDetails
  hasSameBillingAddress?: boolean
  setHasSameBillingAddress?: (value: boolean) => void
  onChange: (address: AddressDetails) => void
}

export const Address = ({
  type,
  addressInfo,
  errors,
  hasSameBillingAddress,
  setHasSameBillingAddress,
  onChange,
}: AddressProps) => {
  const t = useT()

  const values = normalizeAddress(addressInfo)

  const retailer = useRetailerSettingsManager().get()

  const currentCountryId = values.country_id || retailer?.country || ''
  const states = useStates(currentCountryId)
  const currentStateId = values.state || ''
  const currentState = states.find(
    state =>
      state.id === currentStateId.toUpperCase() ||
      state.name.toUpperCase() === currentStateId.toUpperCase()
  )

  // Dynamic label for state/province
  const stateLabelTitle =
    currentCountryId === 'CA'
      ? t('Province', { _context: 'Input title' })
      : t('State', { _context: 'Dropdown title' })

  const stateValidationError = currentStateId
    ? ''
    : t('State is required', { _context: 'Validation error' })
  const countryValidationError = currentCountryId
    ? ''
    : t('Country is required', { _context: 'Validation error' })

  const formTitle =
    type === AddressTypes.Billing
      ? t('Billing address', { _context: 'Form title' })
      : t('Shipping address', { _context: 'Form title' })

  const formLabels = {
    addressLine1: t('Address line 1', {
      _context: 'Input title',
    }),
    addressLine2: t('Address line 2', { _context: 'Input title' }),
    suburb: t('Suburb', { _context: 'Label' }),
    city: t('City', { _context: 'Label' }),
    zipcode: t('ZIP code', { _context: 'Input title' }),
    stateId: t('State', { _context: 'Dropdown title' }),
    countryId: t('Country', { _context: 'Label' }),
  }
  const formPlaceholders = {
    addressLine1: t('Start typing an address...', {
      _context: 'Placeholder text',
    }),
    addressLine2: t('Enter address', { _context: 'Placeholder text' }),
    suburb: t('Enter suburb', { _context: 'Placeholder text' }),
    city: t('Enter city', { _context: 'Placeholder text' }),
    zipcode: t('Enter ZIP code', { _context: 'Placeholder text' }),
    stateId:
      values.country_id === 'CA'
        ? t('Select a province', {
            _context: 'Placeholder text that also serves as a dropdown title',
          })
        : t('Select a state', {
            _context: 'Placeholder text that also serves as a dropdown title',
          }),
    countryId: t('Please select a country', { _context: 'Placeholder text' }),
  }
  const checkboxLabel = t('Billing address matches shipping address', {
    _context: 'Checkbox text',
  })
  const showCheckBox = type === AddressTypes.Shipping

  const addressFullDetails = {
    ...values,
    place_id: '',
    formatted_address: '',
    latitude: 0,
    longitude: 0,
  }

  const handleChange = (field: keyof AddressDetails, value: string) =>
    onChange({ ...values, [field]: value })

  const handleChanges = (address: AddressDetails) =>
    onChange({ ...values, ...address })

  return (
    <fieldset className="vd-fieldset">
      <legend className="vd-legend vd-text-signpost">{formTitle}</legend>
      <FormRow>
        <Column width="6/12" className="vd-pr2">
          <AddressFormAutocomplete
            addressDetails={addressFullDetails}
            setAddressDetails={_ => {}}
            minLengthToFetchSuggestions={3}
            googleMapKey={googleMapKey}
            countryID={
              values.country_id !== ''
                ? values.country_id
                : retailer?.country ?? 'CA'
            }
            handleItemSelectCallback={item => {
              handleChanges(normalizeAddress(item))
            }}
            onValueChangeCallback={value => {
              handleChange('address_1', value)
            }}
            label={formLabels.addressLine1}
            placeHolder={formPlaceholders.addressLine1}
            autoFocus={false}
            hasError={!!errors.address_1}
            formattedAddress={addressFullDetails.address_1}
          />
          {errors.address_1 && <ErrorMessage>{errors.address_1}</ErrorMessage>}
        </Column>
        <Column className="vd-pl2">
          <FormInput
            label={formLabels.addressLine2}
            placeholder={formPlaceholders.addressLine2}
            id={`${type}Address2`}
            value={values.address_2 || ''}
            onChange={e => handleChange('address_2', e)}
            errorMessage={errors.address_2}
          />
        </Column>
      </FormRow>

      <FormRow>
        <Column width="6/12" className="vd-pr2">
          <FormInput
            label={formLabels.suburb}
            placeholder={formPlaceholders.suburb}
            id={`${type}Suburb`}
            value={values.suburb}
            onChange={e => handleChange('suburb', e)}
          />
        </Column>
        <Column className="vd-pl2">
          <FormInput
            label={formLabels.city}
            placeholder={formPlaceholders.city}
            id={`${type}City`}
            value={values.city}
            onChange={e => handleChange('city', e)}
            errorMessage={errors.city}
          />
        </Column>
      </FormRow>

      <FormRow>
        <Column width="6/12" className="vd-pr2">
          <FormInput
            label={formLabels.zipcode}
            placeholder={formPlaceholders.zipcode}
            id={`${type}Postcode`}
            value={values.postcode}
            onChange={e => handleChange('postcode', e)}
            errorMessage={errors.postcode}
          />
        </Column>
        <Column className="vd-pl2">
          {currentCountryId === 'CA' || currentCountryId === 'US' ? (
            <FieldColumn label={stateLabelTitle}>
              <StateDropdown
                required={false}
                country={currentCountryId || 'CA'}
                name={`customer.contact.${type}State`}
                value={currentState}
                label={currentStateId}
                error={stateValidationError || errors.state}
                insideModal
                onSelectCallback={state =>
                  handleChange('state', state?.id || '')
                }
              />
            </FieldColumn>
          ) : (
            <FormInput
              label={formLabels.stateId}
              placeholder={formPlaceholders.stateId}
              id={`${type}State`}
              value={values.state}
              onChange={e => handleChange('state', e)}
              errorMessage={errors.state}
            />
          )}
        </Column>
      </FormRow>

      <FormRow>
        <Column width="6/12" className="vd-pr2">
          <FieldColumn label={formLabels.countryId}>
            <CountryDropdown
              onSelectCallback={country =>
                handleChange('country_id', country?.id || '')
              }
              name={`customer.contact.${type}CountryID`}
              placeholder={formPlaceholders.countryId}
              label={currentCountryId}
              error={countryValidationError || errors.country_id}
              value={currentCountryId}
              insideModal
            />
          </FieldColumn>
        </Column>
      </FormRow>

      {showCheckBox && (
        <FormRow>
          <Column width="6/12" className="vd-pr2">
            <Checkbox
              checked={hasSameBillingAddress}
              label={checkboxLabel}
              onChange={e =>
                setHasSameBillingAddress &&
                setHasSameBillingAddress(e.target.checked)
              }
            />
          </Column>
        </FormRow>
      )}
    </fieldset>
  )
}
